import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  ApiKeyIssueResult,
  ApiUser,
  CreateApiUserInput,
  PaginatedResult,
  SortDirection,
  UpdateApiUserInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface ApiUserListParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDir?: SortDirection;
}

const apiUserKeys = {
  all: ['api-users'] as const,
  list: (params: ApiUserListParams) => ['api-users', 'list', params] as const,
  detail: (id: string) => ['api-users', 'detail', id] as const,
};

export function useApiUsers(params: ApiUserListParams): UseQueryResult<PaginatedResult<ApiUser>> {
  return useQuery({
    queryKey: apiUserKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<ApiUser>>(
        `/api-users${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useCreateApiUser(): UseMutationResult<
  ApiKeyIssueResult,
  unknown,
  CreateApiUserInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateApiUserInput) => api.post<ApiKeyIssueResult>('/api-users', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: apiUserKeys.all });
    },
  });
}

export function useUpdateApiUser(): UseMutationResult<
  ApiUser,
  unknown,
  { id: string; input: UpdateApiUserInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateApiUserInput }) =>
      api.patch<ApiUser>(`/api-users/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: apiUserKeys.all });
    },
  });
}

export function useRevokeApiUser(): UseMutationResult<ApiUser, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<ApiUser>(`/api-users/${id}/revoke`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: apiUserKeys.all });
    },
  });
}

export function useDeleteApiUser(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api-users/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: apiUserKeys.all });
    },
  });
}
